import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { RootState } from '../store/store';
import { statsService } from '../services/statsService';
import {
  ChartBarIcon,
  ClockIcon,
  AcademicCapIcon,
  BookOpenIcon,
  BoltIcon,
  FireIcon,
  CheckCircleIcon,
  XCircleIcon,
  BeakerIcon,
  ChartPieIcon,
} from '@heroicons/react/24/outline';

interface UserStats {
  loginStreak: number;
  totalQuizzesTaken: number;
  totalQuizScore: number;
  averageQuizScore: number;
  bestQuizScore: number;
  worstQuizScore: number;
  collectionsCreated: number;
  flashcardsCreated: number;
  totalStudyTime: number;
  lastStudySession: Date;
  studySessionsCount: number;
  quizzesByDifficulty: {
    easy: number;
    medium: number;
    hard: number;
  };
  lastLoginDate: string;
  lastActivityDate: string;
}

export const Statistics = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Redirect free users to dashboard
    if (!user.isPremium) {
      navigate('/dashboard');
      return;
    }

    const fetchStats = async () => {
      try {
        const data = await statsService.getUserStats();
        setStats(data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    // Refresh stats every 5 minutes if the page is open
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, navigate]);

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0 
      ? `${hours}h ${remainingMinutes}m`
      : `${remainingMinutes}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <NavigationBar />
        <div className="pt-20 pb-12 relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <NavigationBar />
      <div className="pt-20 pb-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Learning Statistics
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Track your progress and achievements
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Login Streak Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
            >
              <div className="flex items-center mb-4">
                <FireIcon className="h-8 w-8 text-orange-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Daily Streak
                </h2>
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {stats?.loginStreak || 0} days
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Last active: {new Date(stats?.lastActivityDate || '').toLocaleDateString()}
              </p>
            </motion.div>

            {/* Study Time Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
            >
              <div className="flex items-center mb-4">
                <ClockIcon className="h-8 w-8 text-green-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Study Time
                </h2>
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {formatTime(stats?.totalStudyTime || 0)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {stats?.studySessionsCount || 0} study sessions
              </p>
            </motion.div>

            {/* Quiz Performance Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
            >
              <div className="flex items-center mb-4">
                <AcademicCapIcon className="h-8 w-8 text-blue-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Quiz Performance</h2>
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {stats?.averageQuizScore ? Math.round(stats.averageQuizScore) : 0}%
              </p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center text-sm">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-gray-600 dark:text-gray-300">Best: {stats?.bestQuizScore || 0}%</span>
                </div>
                <div className="flex items-center text-sm">
                  <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                  <span className="text-gray-600 dark:text-gray-300">Worst: {stats?.worstQuizScore || 0}%</span>
                </div>
              </div>
            </motion.div>

            {/* Content Creation Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
            >
              <div className="flex items-center mb-4">
                <BookOpenIcon className="h-8 w-8 text-purple-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Content Created</h2>
              </div>
              <div className="space-y-2">
                <p className="text-gray-600 dark:text-gray-300">
                  Collections: {stats?.collectionsCreated || 0}
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  Flashcards: {stats?.flashcardsCreated || 0}
                </p>
              </div>
            </motion.div>

            {/* Quiz Details Card (Full Width) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 md:col-span-2 lg:col-span-4"
            >
              <div className="flex items-center mb-6">
                <ChartPieIcon className="h-8 w-8 text-indigo-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Quiz Activity Breakdown</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <BeakerIcon className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Easy Quizzes</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats?.quizzesByDifficulty?.easy || 0}
                    </p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <BeakerIcon className="h-8 w-8 text-yellow-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Medium Quizzes</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats?.quizzesByDifficulty?.medium || 0}
                    </p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <BeakerIcon className="h-8 w-8 text-red-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Hard Quizzes</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats?.quizzesByDifficulty?.hard || 0}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};